import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import useModal from '../../../utils/hooks/useModal';
import useImage from '../../../utils/hooks/useImage';
import cn from '../../../utils/tools/cn';

// Services
import useMediaService from '../../../services/mediaService';

// Components
import Button from '../../atoms/Button/Button';
import MediaGrid from './MediaGrid/MediaGrid';

import styles from './InputMedia.module.scss';
import FlashMessage from '../FlashMessage/FlashMessage';

function InputMedia({
  name, medias, setMedias, min, max, formik,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { getMedias } = useMediaService();

  const { data: mediasList, refetch: refetchMedias } = useQuery('getMedias', getMedias);
  const mediasFromLibrary = useMemo(() => mediasList?.data?.medias || [], [mediasList]);

  const { displayModal, updateModalProps } = useModal();
  const { getImageSrc } = useImage();

  const mediasSelection = useMemo(() => (
    mediasFromLibrary.filter((m) => medias?.some((med) => m.id === med.id)) || []
  ), [medias, mediasFromLibrary]);

  useEffect(() => {
    updateModalProps('mediasFromLibrary', mediasFromLibrary);
  }, [mediasFromLibrary]);

  const handleOpenLibrary = () => {
    displayModal({
      maxWidth: 1440,
      title: t(max && max === 1 ? 'medias.input.selectImage' : 'medias.input.selectImages'),
      content: (
        <MediaGrid
          handleValidateSelection={(selection) => {
            setMedias(selection?.map((media) => ({ id: media.id, fileId: media.fileId })) || []);
          }}
          mediasFromLibrary={mediasFromLibrary}
          max={max}
          min={min}
          preSelection={medias}
          refetchMedias={refetchMedias}
        />
      ),
      fullHeight: true,
    });
  };

  return (
    <>
      <div className={styles.input_wrapper}>
        {mediasSelection?.length ? (
          <div className="m-b-30 m-t-30">
            {mediasSelection?.map((media) => (
              <div className="d-flex m-b-15">
                <a
                  href={getImageSrc({ fileId: media.fileId, widths: { default: isMobile ? 768 : 1440 } })}
                  target="_blank"
                  className={cn([styles.image_holder, 'no-hover'])}
                  rel="noreferrer"
                >
                  <img
                    alt={media.name}
                    src={getImageSrc({
                      fileId: media.fileId,
                      widths: {
                        default: 120,
                      },
                    })}
                  />
                </a>
                <div className="m-l-15">
                  {media.name && media.fileId ? (
                    <a
                      className="d-block fs-14 c-primary-500 text-underline"
                      href={getImageSrc({ fileId: media.fileId, widths: { default: isMobile ? 768 : 1440 } })}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {media.name}
                    </a>
                  ) : null}

                  {media.size ? (
                    <span className="d-block fs-12 fw-300 c-neutral-03 m-t-5">
                      {`${Math.round(media.size / 1000)} Ko`}
                    </span>
                  ) : null}

                  <button
                    type="button"
                    className="c-error-500 fs-12 fw-500 p-t-10 c-pointer hover-dark"
                    onClick={() => {
                      if (name && formik?.setFieldTouched) formik.setFieldTouched(name, true);
                      setMedias(medias?.filter((s) => s.id !== media.id));
                    }}
                  >
                    {t('medias.input.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <Button theme="inverted" onClick={handleOpenLibrary}>
          {mediasSelection?.length ? t('medias.input.editSelection') : (
            max && max === 1 ? t('medias.input.addImage') : t('medias.input.addImages')
          )}
        </Button>
      </div>

      {name && formik?.errors?.[name] && formik?.touched?.[name] ? (
        <div id={`error_${name}`} className="d-flex m-t-20">
          <FlashMessage message={t(formik.errors[name])} color="error" />
        </div>
      ) : null}
    </>
  );
}

InputMedia.propTypes = {
  name: PropTypes.string,
  medias: PropTypes.arrayOf(PropTypes.shape({})),
  setMedias: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  formik: PropTypes.shape({
    setFieldTouched: PropTypes.func,
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
  }),
};

InputMedia.defaultProps = {
  name: null,
  medias: null,
  setMedias: () => {},
  min: null,
  max: null,
  formik: {
    setFieldTouched: () => {},
    errors: {},
    touched: {},
  },
};

export default InputMedia;
